import React, { useState, useEffect, useCallback } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import apolloClient from '../../../graphql/apollo-client';
import { GET_CLIENT_BRANDS } from '../../../graphql/queries';
import { INTEREST_CATEGORIES } from '../../../constants/categories';
import { useAuthStore } from '../../../store/authStore';
import AddBrandModal from '../AddBrandModal';

interface Props {
  control: Control<any>;
  setValue?: any;
  watch?: any;
}

const CampaignBasicInfoV2: React.FC<Props> = ({ control, setValue, watch }) => {
  const { t } = useTranslation(['campaigns', 'common', 'profile']);
  const { user } = useAuthStore();
  const isAgency = user?.userType === 'agency';
  const [brands, setBrands] = useState<any[]>([]);
  const [brandsLoaded, setBrandsLoaded] = useState(false);
  const [showAddBrand, setShowAddBrand] = useState(false);

  const fetchBrands = useCallback(async () => {
    if (!isAgency || !user?.userId) return;
    try {
      const res = await apolloClient.query({
        query: gql`${GET_CLIENT_BRANDS}`,
        variables: { userId: user.userId },
        fetchPolicy: 'network-only',
      });
      if (res.data?.getClientBrands?.success) {
        setBrands(res.data.getClientBrands.brands || []);
      }
    } catch (err) { console.error('Failed to fetch brands:', err); }
    finally { setBrandsLoaded(true); }
  }, [isAgency, user?.userId]);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  const handleBrandCreated = (newBrand: any) => {
    setBrands(prev => [...prev, newBrand]);
    // Auto-select the newly created brand
    if (setValue) {
      setValue('clientBrandId', newBrand.brandId);
      setValue('brandName', newBrand.brandName);
      setValue('clientBrandName', newBrand.brandName);
      // Auto-fill visit address from the new brand's location
      if (newBrand.location) {
        setValue('visitAddress', newBrand.location);
        setValue('visitLatitude', newBrand.latitude ?? null);
        setValue('visitLongitude', newBrand.longitude ?? null);
      }
    }
  };

  const handleBrandSelect = (brandId: string) => {
    if (setValue) {
      setValue('clientBrandId', brandId);
      const selected = brands.find(b => b.brandId === brandId);
      if (selected) {
        setValue('brandName', selected.brandName);
        setValue('clientBrandName', selected.brandName);
        // Auto-fill visit address from the selected brand's location.
        // Only overwrite when the user hasn't already typed something —
        // avoids stomping on edits made before the brand was picked.
        const currentAddress = watch ? (watch('visitAddress') || '') : '';
        if (selected.location && !currentAddress.trim()) {
          setValue('visitAddress', selected.location);
          setValue('visitLatitude', selected.latitude ?? null);
          setValue('visitLongitude', selected.longitude ?? null);
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      <Controller name="brandName" control={control} rules={{ required: true, maxLength: 100 }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.brandName')} <span className="text-red-500">*</span>
            </label>
            <input {...field} type="text" maxLength={100}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${fieldState.error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
              placeholder={t('campaigns:registration.v2.brandNamePlaceholder')} />
            <p className="mt-1 text-xs text-gray-400">{(field.value || '').length}/100</p>
          </div>
        )}
      />

      <Controller name="campaignName" control={control} rules={{ required: true, maxLength: 100 }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.campaignName')} <span className="text-red-500">*</span>
            </label>
            <input {...field} type="text" maxLength={100}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${fieldState.error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
              placeholder={t('campaigns:registration.v2.campaignNamePlaceholder')} />
            <p className="mt-1 text-xs text-gray-400">{(field.value || '').length}/100</p>
          </div>
        )}
      />

      <Controller name="campaignCategory" control={control} rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.campaignCategory')}
            </label>
            <select {...field}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${fieldState.error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}>
              <option value="">{t('common:select')}</option>
              {INTEREST_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{t(`common:categories.${cat}`)}</option>
              ))}
            </select>
          </div>
        )}
      />

      {/* Agency only: Campaign Brand Selector */}
      {isAgency && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('campaigns:registration.v2.campaignBrandSelect')}
          </label>
          <p className="text-xs text-gray-500 mb-2">{t('campaigns:registration.v2.clientSelect')}</p>
          <div className="flex gap-2">
            {brandsLoaded ? (
              <Controller name="clientBrandId" control={control} rules={{ required: isAgency }}
                render={({ field, fieldState }) => (
                  <select
                    value={field.value || ''}
                    onChange={e => { field.onChange(e.target.value); handleBrandSelect(e.target.value); }}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    name={field.name}
                    className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${fieldState.error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}>
                    <option value="">{t('common:select')}</option>
                    {brands.map(b => (
                      <option key={b.brandId} value={b.brandId}>{b.brandName}</option>
                    ))}
                  </select>
                )}
              />
            ) : (
              <div className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-400">
                로딩 중...
              </div>
            )}
            <button type="button" onClick={() => setShowAddBrand(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 whitespace-nowrap">
              {t('profile:myProfile.clientBrands.addBrand')}
            </button>
          </div>
        </div>
      )}

      <AddBrandModal
        isOpen={showAddBrand}
        onClose={() => setShowAddBrand(false)}
        onCreated={handleBrandCreated}
      />
    </div>
  );
};

export default CampaignBasicInfoV2;
